import {
  ActiveTariff,
  getTariffLabel,
} from '@getpopsure/private-health-insurance-pricing-engine';
import { englishFormattedEuroCurrency } from '@getpopsure/public-utility';
import {
  BottomOrRegularModal,
  Button,
  ShareIcon,
  TableInfoButton,
} from '@popsure/dirty-swan';
import classNames from 'classnames';
import Markdown from 'components/markdown';
import { CarouselOption } from 'components/PlanCarousel/PlanCarousel';
import { Calculator } from 'components/PrivateQuoteWidget/Calculator';
import { Coverage } from 'components/PrivateQuoteWidget/Coverage';
import { useIsInView } from 'hooks/useIsInView';
import NextImage from 'next/image';
import { useTranslation } from 'next-i18next';
import React, { useRef, useState } from 'react';
import AnimateHeight from 'react-animate-height';
import { v4 as uuid4 } from 'uuid';

import { ChildCalculator } from '../Calculator/ChildCalculator';
import { PriceLine } from '../Calculator/PriceLine';
import { getEligibilityForShortTerm } from '../util';
import shieldWhite from './assets/shield.svg';
import { DiscountSection } from './components/DiscountSection/DiscountSection';
import { FamilyCashbackModal } from './components/FamilyCashbackModal/FamilyCashbackModal';
import { SaveQuoteModal } from './components/SaveQuoteModal/SaveQuoteModal';
import { TabButton } from './components/TabButton/TabButton';
import { getTranslatedTextData } from './content';
import styles from './FamilyCalculator.module.scss';
import { useCalculatorUrlParams } from './hooks/useCalculatorUrlParams';
import {
  getChildCalculatorOutputData,
  getMainCalculatorOutputData,
  getSpouseCalculatorOutputData,
} from './logic';
import {
  CalculationResultsTextData,
  CalculatorInputData,
  CalculatorOutputData,
  CashbackData,
  ContentVariant,
  FamilyInputDataMap,
  isShortTermPlan,
  PersonKey,
  planConversionMapping,
  PriceData,
} from './model';

const FEATHER_APP_URL = process.env.NEXT_PUBLIC_FEATHER_APP_URL || '';
const preSignupLink = `${FEATHER_APP_URL}/policies/private-health/welcome`;

type CoverageTableType = 'LONG_TERM' | 'SHORT_TERM';

const allPlanOptions: CarouselOption<ActiveTariff>[] = [
  {
    id: 'HiMedicalPlus',
    label: 'Short-term',
  },
  {
    id: 'NKSelectS',
    label: 'Standard plan',
  },
  {
    id: 'NKSelectL',
    label: 'Plus plan',
  },
  {
    id: 'NKSelectXL',
    label: 'Premium plan',
  },
];

const longTermPlanOptions: CarouselOption<ActiveTariff>[] = [
  {
    id: 'NKSelectS',
    label: 'Standard plan',
  },
  {
    id: 'NKSelectL',
    label: 'Plus plan',
  },
  {
    id: 'NKSelectXL',
    label: 'Premium plan',
  },
];

export const FamilyCalculator = () => {
  const { t } = useTranslation();

  const [wantsToCoverFamily, setWantsToCoverFamily] = useState<
    boolean | undefined
  >();
  const [discountApplied, setDiscountApplied] = useState<boolean>(false);
  const [showPriceBreakdownModal, setShowPriceBreakdownModal] = useState(false);
  const [showFamilyCashbackModal, setShowFamilyCashbackModal] = useState(false);
  const [showSaveQuoteModal, setShowSaveQuoteModal] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState<PersonKey>('MAIN');
  const [coverageTableType, setCoverageTableType] =
    useState<CoverageTableType>('LONG_TERM');

  const [discountHasEUPassport, setDiscountHasEUPassport] = useState<
    boolean | undefined
  >();
  const [
    discountHasPermanentResidencyPermit,
    setDiscountHasPermanentResidencyPermit,
  ] = useState<boolean | undefined>();
  const [
    discountIsPlanningToStayMoreThan5Years,
    setDiscountIsPlanningToStayMoreThan5Years,
  ] = useState<boolean | undefined>();

  const [familyMap, setFamilyMap] = useState<FamilyInputDataMap>(
    new Map([['MAIN', {}]])
  );

  const { isSimpleMode, calculatorParams, preSignupPreFillParams } =
    useCalculatorUrlParams({
      familyMap,
      setFamilyMap,
      discountApplied,
      setDiscountApplied,
    });

  const selectedPersonData = familyMap.get(selectedPerson);

  const { birthYear = 0, income, employmentStatus } = selectedPersonData ?? {};

  const calculatorSectionRef = useRef<HTMLDivElement>(null);
  const coverageSectionRef = useRef<HTMLDivElement>(null);

  const { isInView: showHeaderPrice } = useIsInView(coverageSectionRef, 0);

  const createPerson = (personKey: PersonKey) =>
    setFamilyMap((currentMap) => new Map(currentMap).set(personKey, {}));

  const deletePerson = (personKey: PersonKey) =>
    setFamilyMap((currentMap) => {
      const newMap = new Map(currentMap);
      newMap.delete(personKey);
      return newMap;
    });

  const childEntriesArray = Array.from(familyMap.entries()).filter(([key]) =>
    key.includes('CHILD')
  );

  const canCreateChildren = childEntriesArray.length < 5;
  const spouseExists = familyMap.has('SPOUSE');

  const setInputDataValue =
    (attributeName: keyof CalculatorInputData) => (value: unknown) =>
      setFamilyMap((currentMap) =>
        new Map(currentMap).set(selectedPerson, {
          ...currentMap.get(selectedPerson),
          [attributeName]: value,
        })
      );

  const mainOutputData = getMainCalculatorOutputData({
    isSimpleMode,
    calculatorInputData: familyMap.get('MAIN'),
  });

  const mainOnHanseMerkur = !!mainOutputData.contentVariant?.includes(
    'HANSE_MERKUR_REDIRECT'
  );
  const mainHasAnsweredAllQuestions =
    !!mainOutputData.priceData || mainOutputData.contentVariant !== 'NO_PRICE';
  const mainIsNotEligible = !mainOnHanseMerkur && !mainOutputData.priceData;

  const mainIsAboveIncomeTreshold = !!mainOutputData.priceData;

  const spouseOutputData = spouseExists
    ? getSpouseCalculatorOutputData({
        isSimpleMode,
        calculatorInputData: familyMap.get('SPOUSE'),
        mainHasAnsweredAllQuestions,
        mainIsNotEligible,
        mainOnHanseMerkur,
        unusedMainEmployerContributions:
          mainOutputData.priceData?.unusedEmployerContributions,
      })
    : undefined;

  const childCount = childEntriesArray.length;
  let unusedEmployerContributionsAmountForChildren = 0;

  if (childCount > 0) {
    const spouseIsDependent = spouseOutputData?.priceData?.isDependent;

    const mainUnusedEmployerContributionsAmount =
      mainOutputData?.priceData?.unusedEmployerContributions?.base ?? 0;

    const spouseUnusedEmployerContributionsAmount =
      spouseOutputData?.priceData?.unusedEmployerContributions?.base ?? 0;

    unusedEmployerContributionsAmountForChildren = spouseIsDependent
      ? spouseUnusedEmployerContributionsAmount
      : spouseUnusedEmployerContributionsAmount +
        mainUnusedEmployerContributionsAmount;
  }

  const childrenOutputDataMap = new Map(
    childEntriesArray.map(([key, inputData]) => [
      key,
      getChildCalculatorOutputData({
        isSimpleMode,
        calculatorInputData: inputData,
        mainHasAnsweredAllQuestions,
        mainIsNotEligible,
        mainOnHanseMerkur,
        unusedParentEmployerContributions: {
          base: unusedEmployerContributionsAmountForChildren / childCount,
          longTermCare: 0,
        },
      }),
    ])
  );

  let selectedPersonOutputData:
    | CalculatorOutputData<ContentVariant>
    | undefined = mainOutputData;

  if (selectedPerson === 'SPOUSE') {
    selectedPersonOutputData = spouseOutputData;
  }

  if (selectedPerson.includes('CHILD')) {
    selectedPersonOutputData = childrenOutputDataMap.get(selectedPerson);
  }

  const selectedPersonPrice =
    selectedPersonOutputData?.priceData?.discountedMonthlyPrice ??
    selectedPersonOutputData?.priceData?.monthlyPrice;

  const selectedPersonEmployerPaysBackAmount = selectedPersonOutputData
    ?.priceData?.discountedMonthlyPrice
    ? Math.round(selectedPersonOutputData.priceData.monthlyPrice) -
      Math.round(selectedPersonOutputData.priceData.discountedMonthlyPrice)
    : undefined;

  const textData = selectedPersonOutputData?.contentVariant
    ? getTranslatedTextData({ t, birthYear })[
        selectedPersonOutputData?.contentVariant
      ]
    : undefined;

  const selectedPersonResultsText: CalculationResultsTextData | undefined =
    selectedPersonOutputData?.contentVariant !== 'NO_PRICE' && textData
      ? {
          ...textData,
        }
      : undefined;

  const childrenPriceDataArr = Array.from(
    childrenOutputDataMap,
    ([_, c]) => c.priceData
  ).filter(Boolean) as PriceData[];

  const discountedChildrenTotal = childrenPriceDataArr.reduce(
    (sum, priceData) => sum + (priceData?.discountedMonthlyPrice ?? 0),
    0
  );

  const childrenTotal = childrenPriceDataArr.reduce(
    (sum, priceData) => sum + (priceData?.monthlyPrice ?? 0),
    0
  );

  const discountedFamilyTotal =
    Math.round(
      mainOutputData.priceData?.discountedMonthlyPrice ??
        mainOutputData.priceData?.monthlyPrice ??
        0
    ) +
    Math.round(
      spouseOutputData?.priceData?.discountedMonthlyPrice ??
        spouseOutputData?.priceData?.monthlyPrice ??
        0
    ) +
    Math.round(discountedChildrenTotal ?? childrenTotal);

  const familyTotal =
    Math.round(mainOutputData.priceData?.monthlyPrice ?? 0) +
    Math.round(spouseOutputData?.priceData?.monthlyPrice ?? 0) +
    Math.round(childrenTotal);

  const employerPaysBackAmount =
    familyTotal > discountedFamilyTotal
      ? familyTotal - discountedFamilyTotal
      : undefined;

  const showFamilyTotal =
    familyTotal > Math.round(mainOutputData?.priceData?.monthlyPrice ?? 0);

  const familyInputDataArray = Array.from(familyMap.values());

  const showFamilyCoverage = spouseExists || childCount > 0;

  const everyoneIsOnLongTerm = familyInputDataArray.every(
    ({
      isPlanningToStayMoreThan5Years,
      hasPermanentResidencyPermit,
      hasEUPassport,
    }) =>
      hasEUPassport ||
      hasPermanentResidencyPermit ||
      isPlanningToStayMoreThan5Years !== undefined
        ? !getEligibilityForShortTerm({
            isPlanningToStayMoreThan5Years,
            hasEUPassport,
            hasPermanentResidencyPermit,
          })
        : true
  );

  const everyoneIsOnShortTerm = familyInputDataArray.every(
    ({
      isPlanningToStayMoreThan5Years,
      hasPermanentResidencyPermit,
      hasEUPassport,
    }) =>
      hasEUPassport ||
      hasPermanentResidencyPermit ||
      isPlanningToStayMoreThan5Years !== undefined
        ? getEligibilityForShortTerm({
            isPlanningToStayMoreThan5Years,
            hasEUPassport,
            hasPermanentResidencyPermit,
          })
        : true
  );

  const showPrivateLight =
    (isSimpleMode && discountApplied) ||
    (!isSimpleMode && everyoneIsOnShortTerm) ||
    (!isSimpleMode &&
      !everyoneIsOnShortTerm &&
      !everyoneIsOnLongTerm &&
      coverageTableType === 'SHORT_TERM');

  const mainCashbackData = mainOutputData.priceData?.cashbackData;
  const spouseCashbackData = spouseOutputData?.priceData?.cashbackData;
  const childCashbackDataArray = childrenPriceDataArr
    .map((priceData) => priceData?.cashbackData)
    .filter(Boolean) as CashbackData[];

  const familyPotentialCashback =
    (mainCashbackData?.totalPotentialCashback ?? 0) +
    (spouseCashbackData?.totalPotentialCashback ?? 0) +
    childCashbackDataArray.reduce(
      (sum, { totalPotentialCashback }) => sum + totalPotentialCashback,
      0
    );

  const familyPublicCost =
    (mainOutputData?.priceData?.publicPrice ?? 0) +
    (spouseOutputData?.priceData?.publicPrice ?? 0) +
    childrenPriceDataArr.reduce(
      (sum, { publicPrice }) => sum + (publicPrice ?? 0),
      0
    );

  const mainPrice =
    mainOutputData?.priceData?.discountedMonthlyPrice ??
    mainOutputData?.priceData?.monthlyPrice;

  const spousePrice =
    spouseOutputData?.priceData?.discountedMonthlyPrice ??
    spouseOutputData?.priceData?.monthlyPrice;

  const showFooter = familyTotal > 0;

  const selectedPersonEligibleForShortTerm = getEligibilityForShortTerm({
    isPlanningToStayMoreThan5Years:
      selectedPersonData?.isPlanningToStayMoreThan5Years,
    hasEUPassport: selectedPersonData?.hasEUPassport,
    hasPermanentResidencyPermit:
      selectedPersonData?.hasPermanentResidencyPermit,
  });

  const planCarouselOptions =
    (isSimpleMode && discountApplied) || selectedPersonEligibleForShortTerm
      ? allPlanOptions
      : longTermPlanOptions;

  return (
    <div id="calculator">
      {showSaveQuoteModal && (
        <SaveQuoteModal
          isOpen={showSaveQuoteModal}
          onClose={() => setShowSaveQuoteModal(false)}
          mainPriceData={mainOutputData.priceData}
          spousePriceData={spouseOutputData?.priceData}
          childrenPriceDataArr={childrenPriceDataArr}
          familyMap={familyMap}
          calculatorParams={calculatorParams}
        />
      )}

      <BottomOrRegularModal
        isOpen={showPriceBreakdownModal}
        title={t(
          'privatehealth:calculator.priceBreakdown.title',
          'How is my price calculated?'
        )}
        onClose={() => setShowPriceBreakdownModal(false)}
      >
        <Markdown styling={{ p: 'p-p pt16 px24 wmx6' }}>
          {t(
            'privatehealth:calculator.priceBreakdown.description',
            'Cost is calculated based on the age at sign-up and does not increase based on your age as you get older. **There are no price increases due to newly developed medical conditions**.\n\nThe only time insurance public and private health prices change is when there’s an overall increase in healthcare costs. Historically, the costs have increased 2% per year.'
          )}
        </Markdown>

        {!!familyTotal && (
          <PriceLine
            label="Family price"
            price={familyTotal}
            className="mt32"
          />
        )}

        {employerPaysBackAmount && (
          <PriceLine
            label="Employer pays back"
            price={employerPaysBackAmount}
            minus
          />
        )}

        <div className={`bg-grey-200 mt16 mb16 ${styles.divider}`} />

        {discountedFamilyTotal && (
          <PriceLine label="Family total" price={discountedFamilyTotal} bold />
        )}

        {!!mainPrice && (
          <PriceLine
            label="You"
            sublabel={`${getTariffLabel(
              familyMap.get('MAIN')?.plan ?? 'NKSelectS'
            )} plan`}
            price={mainPrice}
          />
        )}

        {!!spousePrice && (
          <PriceLine
            label="Spouse"
            sublabel={`${getTariffLabel(
              familyMap.get('SPOUSE')?.plan ?? 'NKSelectS'
            )} plan`}
            price={spousePrice}
          />
        )}

        {childEntriesArray.flatMap(([childKey], index) => {
          const childPriceData = childrenOutputDataMap.get(childKey)?.priceData;
          const childPlan = familyMap.get(childKey)?.plan;

          return childPriceData && childPlan
            ? [
                <PriceLine
                  key={childKey}
                  label={
                    childEntriesArray.length > 1
                      ? `Child ${index + 1}`
                      : 'Child'
                  }
                  sublabel={`${getTariffLabel(childPlan)} plan`}
                  price={
                    childPriceData.discountedMonthlyPrice ??
                    childPriceData.monthlyPrice
                  }
                />,
              ]
            : [];
        })}
        <div className="mb48" />
      </BottomOrRegularModal>
      <FamilyCashbackModal
        title={t(
          'privatehealth:calculator.familyCashbackModal.title',
          'Cashback'
        )}
        mainCashbackData={mainCashbackData}
        spouseCashbackData={spouseCashbackData}
        childCashbackDataArray={childCashbackDataArray}
        isOpen={showFamilyCashbackModal}
        onClose={() => setShowFamilyCashbackModal(false)}
      />
      <section
        ref={calculatorSectionRef}
        className={`bg-grey-100 mt56 ${styles.section}`}
      >
        <div className="p-container">
          <h2 className="p-h2 p--serif mb24 fw-bold">
            {t('privatehealth:calculator.title', 'See our prices and coverage')}
          </h2>

          <div className="d-flex mb8 gap8 f-wrap">
            <TabButton
              caption="You"
              variation={selectedPerson === 'MAIN' ? 'SELECTED' : 'UNSELECTED'}
              onClick={() => setSelectedPerson('MAIN')}
            />

            {spouseExists && (
              <TabButton
                caption="Spouse"
                variation={
                  selectedPerson === 'SPOUSE' ? 'SELECTED' : 'UNSELECTED'
                }
                onClick={() => setSelectedPerson('SPOUSE')}
                onRemoveButtonClick={() => {
                  if (selectedPerson === 'SPOUSE') {
                    setSelectedPerson('MAIN');
                  }
                  deletePerson('SPOUSE');
                }}
                showRemoveButton
              />
            )}

            {childEntriesArray.map(([childKey], index) => (
              <TabButton
                key={childKey}
                caption={
                  childEntriesArray.length > 1 ? `Child ${index + 1}` : 'Child'
                }
                variation={
                  selectedPerson === childKey ? 'SELECTED' : 'UNSELECTED'
                }
                onClick={() => setSelectedPerson(childKey)}
                onRemoveButtonClick={() => {
                  if (selectedPerson === childKey) {
                    setSelectedPerson('MAIN');
                  }
                  deletePerson(childKey);
                }}
                showRemoveButton
              />
            ))}

            {!spouseExists && (
              <TabButton
                caption="Spouse"
                variation="ADD"
                onClick={() => {
                  createPerson('SPOUSE');
                  setSelectedPerson('SPOUSE');

                  if (
                    discountApplied &&
                    wantsToCoverFamily !== undefined &&
                    !wantsToCoverFamily
                  ) {
                    setDiscountApplied(false);
                    setDiscountHasEUPassport(undefined);
                    setDiscountHasPermanentResidencyPermit(undefined);
                    setDiscountIsPlanningToStayMoreThan5Years(undefined);
                  }
                }}
              />
            )}

            {canCreateChildren && (
              <TabButton
                caption="Child"
                variation="ADD"
                onClick={() => {
                  const childKey: PersonKey = `CHILD-${uuid4()}`;
                  createPerson(childKey);
                  setSelectedPerson(childKey);

                  if (
                    discountApplied &&
                    wantsToCoverFamily !== undefined &&
                    !wantsToCoverFamily
                  ) {
                    setDiscountApplied(false);
                    setDiscountHasEUPassport(undefined);
                    setDiscountHasPermanentResidencyPermit(undefined);
                    setDiscountIsPlanningToStayMoreThan5Years(undefined);
                  }
                }}
              />
            )}
          </div>
        </div>

        {!selectedPerson.includes('CHILD') ? (
          <Calculator
            key={selectedPerson}
            personKey={selectedPerson}
            inputData={familyMap.get(selectedPerson) || {}}
            setInputDataValue={setInputDataValue}
            resultsText={selectedPersonResultsText}
            price={selectedPersonPrice}
            employerPaysBackAmount={selectedPersonEmployerPaysBackAmount}
            isSimpleMode={isSimpleMode}
            planCarouselOptions={planCarouselOptions}
            smallerPriceFont={showFamilyTotal}
          />
        ) : (
          <ChildCalculator
            key={selectedPerson}
            inputData={familyMap.get(selectedPerson) || {}}
            setInputDataValue={setInputDataValue}
            resultsText={selectedPersonResultsText}
            price={selectedPersonPrice}
            employerPaysBackAmount={selectedPersonEmployerPaysBackAmount}
            isSimpleMode={isSimpleMode}
            planCarouselOptions={planCarouselOptions}
            smallerPriceFont={showFamilyTotal}
          />
        )}
        {isSimpleMode && (
          <DiscountSection
            hasEUPassport={discountHasEUPassport}
            isPlanningToStayMoreThan5Years={
              discountIsPlanningToStayMoreThan5Years
            }
            hasPermanentResidencyPermit={discountHasPermanentResidencyPermit}
            setHasEUPassport={setDiscountHasEUPassport}
            setHasPermanentResidencyPermit={
              setDiscountHasPermanentResidencyPermit
            }
            setIsPlanningToStayMoreThan5Years={
              setDiscountIsPlanningToStayMoreThan5Years
            }
            isFamily={showFamilyCoverage}
            wantsToCoverFamily={wantsToCoverFamily}
            setWantsToCoverFamily={setWantsToCoverFamily}
            discountApplied={discountApplied}
            onApplyDiscount={() => {
              const familyArray = Array.from(familyMap);
              const familyArrayWithShortTermPlans: [
                PersonKey,
                CalculatorInputData
              ][] = familyArray.map(([personKey, inputData]) => [
                personKey,
                {
                  ...inputData,
                  plan:
                    !isShortTermPlan(inputData.plan) && inputData.plan
                      ? planConversionMapping[inputData.plan]
                      : inputData.plan ?? 'HiMedical',
                },
              ]);
              setFamilyMap(new Map(familyArrayWithShortTermPlans));
              setDiscountApplied(true);
            }}
            onRemoveDiscount={() => {
              const familyArray = Array.from(familyMap);
              const familyArrayWithLongTermPlans: [
                PersonKey,
                CalculatorInputData
              ][] = familyArray.map(([personKey, inputData]) => [
                personKey,
                {
                  ...inputData,
                  plan: isShortTermPlan(inputData.plan)
                    ? planConversionMapping[inputData.plan]
                    : inputData.plan ?? 'NKSelectS',
                },
              ]);
              setFamilyMap(new Map(familyArrayWithLongTermPlans));
              setDiscountApplied(false);
            }}
          />
        )}

        <AnimateHeight
          duration={300}
          height={showFamilyTotal ? 'auto' : 0}
          className="w100"
        >
          <div className="p-container d-flex ai-baseline jc-end c-gap48 r-gap16 mt16 f-wrap">
            <div className={`d-flex gap8 ai-baseline jc-end ${styles.price}`}>
              <p className="p-p tc-grey-600">
                {t(
                  'privatehealth:calculator.publicTotal',
                  'Public health would cost'
                )}{' '}
                <span className="fw-bold">
                  {englishFormattedEuroCurrency(
                    Math.round(familyPublicCost),
                    true
                  )}{' '}
                  /mo
                </span>
              </p>
            </div>
            {familyPotentialCashback > 0 && familyMap.size > 1 && (
              <div className={`d-flex gap8 ai-baseline jc-end ${styles.price}`}>
                <div className="d-flex gap8 ai-center">
                  <p className="p-p tc-grey-600">
                    {t(
                      'privatehealth:calculator.familyPotentialCashback',
                      'Cashback'
                    )}{' '}
                    <span className="fw-bold">
                      {englishFormattedEuroCurrency(
                        Math.round(familyPotentialCashback),
                        true
                      )}{' '}
                      /yr
                    </span>
                  </p>
                  <TableInfoButton
                    onClick={() => setShowFamilyCashbackModal(true)}
                  />
                </div>
              </div>
            )}
            <div
              className={`d-flex gap8 ai-baseline jc-end ${styles.price} ${styles.familyTotal}`}
            >
              <p className="p-h3">
                {t('privatehealth:calculator.familyTotal', 'Family total')}
              </p>
              <h3 className="p-h1--xl p--serif tc-primary-500">
                {englishFormattedEuroCurrency(
                  Math.round(discountedFamilyTotal),
                  true
                )}
                <span className="p-p tc-primary-500"> /mo</span>
              </h3>
              <TableInfoButton
                className={styles.priceBreakdownButton}
                onClick={() => setShowPriceBreakdownModal(true)}
              />
            </div>
          </div>
        </AnimateHeight>
      </section>
      <AnimateHeight
        duration={300}
        height={!(everyoneIsOnLongTerm || everyoneIsOnShortTerm) ? 'auto' : 0}
        className="w100"
      >
        <div className=" bg-grey-100">
          <div className="p-container d-flex gap8 pb8 f-wrap">
            <TabButton
              caption="Long-term"
              variation={
                coverageTableType === 'LONG_TERM' ? 'SELECTED' : 'UNSELECTED'
              }
              onClick={() => setCoverageTableType('LONG_TERM')}
            />
            <TabButton
              caption="Short-term"
              variation={
                coverageTableType === 'SHORT_TERM' ? 'SELECTED' : 'UNSELECTED'
              }
              onClick={() => setCoverageTableType('SHORT_TERM')}
            />
          </div>
        </div>
      </AnimateHeight>
      <section id="coverage-overview" ref={coverageSectionRef}>
        <Coverage
          isFamily={showFamilyCoverage}
          isAboveIncomeThreshold={mainIsAboveIncomeTreshold}
          isPrivateLight={showPrivateLight}
          birthYear={birthYear}
          income={income}
          employmentStatus={employmentStatus}
          showHeaderPrice={showHeaderPrice}
          ctaAction={() => {
            if (calculatorSectionRef.current) {
              window.scrollTo({
                top: calculatorSectionRef.current.offsetTop,
                behavior: 'smooth',
              });
            }
          }}
        />
      </section>
      {showFooter && (
        <footer
          className={classNames(
            'w100 d-flex jc-center ai-center bg-purple-500',
            styles.fixedPriceBar
          )}
        >
          <div
            className={classNames(
              'p-body jc-between ai-center ws12',
              styles.desktopSticky
            )}
          >
            <div className="d-flex ai-center gap8">
              <NextImage
                src={shieldWhite}
                alt="icon of a shield"
                width={24}
                height={24}
              />
              <div className="d-flex ai-baseline gap8 ">
                <h3 className="p-h3 tc-white">
                  {showFamilyTotal
                    ? 'Family coverage'
                    : `${getTariffLabel(
                        familyMap.get('MAIN')?.plan ?? 'NKSelectS'
                      )} plan`}
                </h3>
                <p className="p--serif p-p--small tc-white">
                  <span className="p-h2 tc-white">
                    {englishFormattedEuroCurrency(discountedFamilyTotal, true)}
                  </span>
                  /mo
                </p>
              </div>
            </div>
            <div className="d-flex ai-center gap16">
              <div className="d-flex ai-center gap16">
                <Button
                  variant="outlineWhite"
                  onClick={() => setShowSaveQuoteModal(true)}
                  type="button"
                >
                  Save quote
                </Button>
                <a
                  className="p-btn--secondary-white wmn3"
                  href={`${preSignupLink}${preSignupPreFillParams}`}
                >
                  Get covered
                </a>
              </div>
            </div>
          </div>
          <div
            className={classNames(
              'p-body ai-center jc-center gap16 w100',
              styles.mobileSticky
            )}
          >
            <a
              className="p-btn--secondary-white wmn3"
              href={`${preSignupLink}${preSignupPreFillParams}`}
            >
              Get covered
            </a>
            <Button
              variant="outlineWhite"
              onClick={() => setShowSaveQuoteModal(true)}
              hideLabel
              type="button"
            >
              <ShareIcon size={24} />
            </Button>
          </div>
        </footer>
      )}
    </div>
  );
};
